<template>
  <div class="p-6">
    <!-- Render definitions markdown content -->
    <div v-if="renderedMarkdown" class="markdown-content" v-html="renderedMarkdown" @click="handleInternalLink"></div>
    
    <!-- Loading state -->
    <div v-else-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <i class="pi pi-spin pi-spinner text-3xl mb-4" style="color: var(--primary-gold);"></i>
        <p style="color: var(--text-secondary);">Loading definitions...</p>
      </div>
    </div>
    
    <!-- Error state -->
    <div v-else-if="error" class="text-center py-12">
      <i class="pi pi-exclamation-triangle text-4xl mb-4" style="color: var(--text-danger);"></i>
      <p style="color: var(--text-secondary);">{{ error }}</p>
    </div>

    <!-- Empty state -->
    <div v-else class="text-center py-12">
      <i class="pi pi-info-circle text-4xl mb-4" style="color: var(--text-muted);"></i>
      <p style="color: var(--text-secondary);">No definitions available for this section.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import type { Section } from '@/types/ashmatics';
import { useMarkdown } from '@/composables/useMarkdown';

const props = defineProps<{
  section: Section | null;
}>();

const { renderMarkdown, handleInternalLink: markdownHandleInternalLink, parseFrontmatter } = useMarkdown();

// State
const definitionsContent = ref<string>('');
const isLoading = ref<boolean>(false);
const error = ref<string>('');

// Handle internal link clicks
const handleInternalLink = (event: MouseEvent) => {
  markdownHandleInternalLink(event);
};

// Render the definitions markdown content
const renderedMarkdown = computed(() => {
  if (!definitionsContent.value) return '';

  // Strip YAML frontmatter before rendering
  const { content } = parseFrontmatter(definitionsContent.value);
  return renderMarkdown(content);
});

// Load definitions markdown content from section data
const loadDefinitions = () => {
  if (!props.section) return;

  isLoading.value = true;
  error.value = '';

  try {
    // All sections (including 0.1) should have definitions in tab_contents
    // The content is pre-loaded by the API when the domain data is fetched
    if (props.section.tab_contents?.definitions) {
      definitionsContent.value = props.section.tab_contents.definitions;
    } else {
      error.value = 'No definitions available for this section.';
    }
  } catch (err) {
    console.error('Error loading definitions:', err);
    error.value = `Failed to load definitions: ${err instanceof Error ? err.message : 'Unknown error'}`;
  } finally {
    isLoading.value = false;
  }
};

// Load definitions when component mounts or section changes
onMounted(() => {
  loadDefinitions();
});

// Watch for section changes and reload definitions
import { watch } from 'vue';
watch(() => props.section, () => {
  if (props.section) {
    loadDefinitions();
  }
}, { immediate: false });
</script>

<style scoped>
/* Markdown content styling */
.markdown-content {
  line-height: 1.7;
  color: var(--text-primary);
}

.markdown-content :deep(h1) {
  font-size: 2rem;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
}

.markdown-content :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.markdown-content :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.markdown-content :deep(h4) {
  font-size: 1.125rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.markdown-content :deep(p) {
  margin-bottom: 1rem;
  color: var(--text-secondary);
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  color: var(--text-secondary);
}

.markdown-content :deep(li) {
  margin-bottom: 0.25rem;
}

.markdown-content :deep(blockquote) {
  border-left: 4px solid var(--primary-gold);
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: var(--text-secondary);
}

.markdown-content :deep(code) {
  background: var(--surface-100);
  color: var(--primary-color);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.markdown-content :deep(pre) {
  background: var(--surface-100);
  color: var(--text-primary);
  padding: 1rem;
  border-radius: 0.375rem;
  overflow-x: auto;
  margin: 1rem 0;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.markdown-content :deep(pre code) {
  background: none;
  padding: 0;
  color: inherit;
}

.markdown-content :deep(a) {
  color: var(--primary-color);
  text-decoration: none;
}

.markdown-content :deep(a:hover) {
  text-decoration: underline;
}

.markdown-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  border: 1px solid var(--surface-300);
  padding: 0.5rem;
  text-align: left;
}

.markdown-content :deep(th) {
  background: var(--surface-100);
  font-weight: 600;
  color: var(--text-primary);
}

.markdown-content :deep(td) {
  color: var(--text-secondary);
}

.markdown-content :deep(hr) {
  border: none;
  height: 1px;
  background: var(--surface-300);
  margin: 2rem 0;
}

/* Framework-specific styling */
.markdown-content :deep(strong) {
  color: var(--text-primary);
  font-weight: 600;
}

.markdown-content :deep(em) {
  color: var(--text-secondary);
  font-style: italic;
}
</style>